import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle2, Users, Heart, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export const Petition = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    zipCode: '',
    relationship: 'community_member',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signatureCount, setSignatureCount] = useState(0);

  useEffect(() => {
    fetchSignatureCount();
  }, []);

  const fetchSignatureCount = async () => {
    try {
      const response = await axios.get(`${API}/petition/count`);
      setSignatureCount(response.data.count);
    } catch (err) {
      console.error('Error fetching signature count:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/petition/sign`, formData);
      setSubmitted(true);
      fetchSignatureCount();
      setFormData({
        name: '',
        email: '',
        zipCode: '',
        relationship: 'community_member',
        message: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit signature. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="petition" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-tealAccent/5 via-white to-goldenOlive/5">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-tealAccent/10 px-6 py-3 rounded-full mb-6">
            <Heart className="text-tealAccent" size={24} />
            <span className="text-tealAccent font-bold text-lg">Show Your Support</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">Support Doctrina Charter School</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Help us bring educator-led, community-centered education to the Inland Empire. Your signature shows the authorizing board that our community wants Doctrina.
          </p>
        </div>

        {/* Signature Counter */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="border-2 border-tealAccent/20 bg-gradient-to-br from-tealAccent/10 to-white shadow-xl">
            <CardContent className="p-8 text-center">
              <Users className="w-16 h-16 text-tealAccent mx-auto mb-4" />
              <div className="text-6xl font-bold text-tealAccent mb-2">{signatureCount}</div>
              <p className="text-xl text-gray-700 font-semibold">Community Supporters</p>
              <div className="flex items-center justify-center mt-4 text-sageGreen">
                <TrendingUp size={20} className="mr-2" />
                <span className="font-medium">Growing daily!</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left Side - Why Sign */}
          <div>
            <h3 className="text-3xl font-bold text-navyBlue mb-6">Why Your Signature Matters</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="text-tealAccent flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-bold text-navyBlue text-lg mb-1">Proves Community Demand</h4>
                  <p className="text-gray-600">Your signature demonstrates to the authorizing board that families want Doctrina in the Inland Empire.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="text-goldenOlive flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-bold text-navyBlue text-lg mb-1">Strengthens Our Petition</h4>
                  <p className="text-gray-600">Community support is a critical component of our charter application process.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="text-sageGreen flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-bold text-navyBlue text-lg mb-1">Shows You Care</h4>
                  <p className="text-gray-600">Stand with educators and families who believe in transformative middle school education.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle2 className="text-coralRed flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="font-bold text-navyBlue text-lg mb-1">Stay Informed</h4>
                  <p className="text-gray-600">Get updates on our progress toward opening our founding campus in Fall 2027 and our full vision in the years ahead.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-navyBlue/5 rounded-2xl">
              <p className="text-sm text-gray-600 italic">
                <strong>Your information is private.</strong> We will only use your contact details to update you on Doctrina's progress and will share aggregate data (not personal information) with the authorizing board.
              </p>
            </div>
          </div>

          {/* Right Side - Signature Form */}
          <div>
            {submitted ? (
              <Card className="border-2 border-tealAccent bg-tealAccent/5 shadow-xl">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-20 h-20 text-tealAccent mx-auto mb-6" />
                  <h3 className="text-3xl font-bold text-navyBlue mb-4">Thank You!</h3>
                  <p className="text-lg text-gray-700 mb-6">
                    Your support means everything to us. Together, we'll bring educator-led innovation to the Inland Empire!
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="bg-tealAccent hover:bg-tealAccent/90 text-white font-semibold px-8 py-6 text-lg rounded-xl"
                  >
                    Sign for Another Person
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-2 border-navyBlue/10 shadow-xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-navyBlue mb-6">Add Your Signature</h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-tealAccent focus:ring-2 focus:ring-tealAccent/20 outline-none transition-all"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-tealAccent focus:ring-2 focus:ring-tealAccent/20 outline-none transition-all"
                        placeholder="your.email@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        required
                        value={formData.zipCode}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-tealAccent focus:ring-2 focus:ring-tealAccent/20 outline-none transition-all"
                        placeholder="92585"
                        maxLength="5"
                      />
                    </div>

                    <div>
                      <label htmlFor="relationship" className="block text-sm font-semibold text-gray-700 mb-2">
                        I am a... *
                      </label>
                      <select
                        id="relationship"
                        name="relationship"
                        required
                        value={formData.relationship}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-tealAccent focus:ring-2 focus:ring-tealAccent/20 outline-none transition-all"
                      >
                        <option value="parent">Parent/Guardian</option>
                        <option value="educator">Educator</option>
                        <option value="community_member">Community Member</option>
                        <option value="business_owner">Business Owner</option>
                        <option value="student">Student</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Why do you support Doctrina? (Optional)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-tealAccent focus:ring-2 focus:ring-tealAccent/20 outline-none transition-all resize-none"
                        placeholder="Share why you believe in Doctrina's mission..."
                      />
                    </div>

                    {error && (
                      <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                        <p className="text-red-600 text-sm">{error}</p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-tealAccent hover:bg-tealAccent/90 text-white font-bold py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                    >
                      {loading ? 'Submitting...' : 'Sign the Petition'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Petition;
